<template>
  <div
    ref="table-payment-container"
    class="relative flex-1 overflow-auto rounded-md border border-slate-300 dark:border-slate-300 dark:bg-slate-700 dark:text-gray-300"
  >
    <table
      v-if="payments.length"
      class="dashboard-table"
      style="border-spacing: 0"
    >
      <thead>
        <draggable
          id="dragger"
          v-model="localColumns"
          item-key="name"
          tag="tr"
          :component-data="{ as: 'transition-group' }"
          :revert-on-spill="false"
          :draggable="'.draggable-cell'"
          :move="handleMove"
          @update="handleDrag"
        >
          <template #item="{ index }">
            <DashBoardHeadColumnComponent
              v-model:column="columns[index]"
              v-model:sort="localSort"
              v-model:cell-width="cellWidth"
              v-model:require-save-dashboard="localRequireSaveDashboard"
              :index="index"
              :is-last="columns.length === index + 1"
              :resize-able="columns.length > 1"
              :sum-able="true"
              :search-able="true"
              :available-column-fields="availableFields"
              @delete:column="handleDeleteColumns"
              @update:dashboard="updateDashboard"
            />
          </template>
        </draggable>
      </thead>
      <tbody>
        <PaymentRowComponent
          v-for="(payment, indexPayment) in localPaymentsPaginated"
          :key="payment.id"
          :payment="payment"
          :columns="columns"
          :cell-width="cellWidth"
          :index-payment="indexPayment"
          :is-last="indexPayment === payments.length - 1 && !hasSumAbleColumns"
        />
        <tr v-if="columns && countPayments > page * pageSize">
          <td class="pagination" :colspan="columns.length">
            <div
              class="flex flex-row gap-3"
              :style="{ width: `${visibleWidthTable}px` }"
            >
              <button
                class="bg-secondary text-contrast-secondary flex w-full cursor-pointer flex-row items-center justify-center gap-1 px-1 py-2 text-xs font-semibold"
                @click="page++"
              >
                <span>{{ $t('global.display_more') }}</span>
                <span>{{ page * pageSize }}/{{ countPayments }}</span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="hasSumAbleColumns" class="sticky bottom-0 z-30">
        <PaymentRowComponent
          v-if="true"
          :payment="paymentsSums"
          :columns="sumColumns"
          :cell-width="cellWidthComputed"
          :index-payment="1"
          :is-last="true"
          :is-sum="true"
          :force-bold="true"
        />
      </tfoot>
    </table>
    <div v-else class="flex h-full w-full items-center justify-center">
      <h4>No result</h4>
    </div>
  </div>
</template>
<script>
import DashBoardHeadColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/DashBoardHeadColumnComponent'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import PaymentRowComponent from '@c/Dashboard/Content/Payment/components/PaymentRowComponent.vue'

export default {
  name: 'PaymentTable',
  components: {
    PaymentRowComponent,
    DashBoardHeadColumnComponent,
    draggable
  },
  props: {
    payments: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: false,
      default: () => {}
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'update:columns',
    'update:require-save-dashboard',
    'update:sort',
    'update:dashboard',
    'fetch-payments',
    'refresh-click'
  ],
  data() {
    const cellWidth = {}

    for (let key in this.columns) {
      cellWidth[key] = this.columns[key].visual?.width || 'auto'
    }

    return {
      cellWidth,
      pageSize: 100,
      page: 1,
      currentSort: '',
      localpayments: [...this.payments],
      visibleWidthTable: 0
    }
  },
  computed: {
    ...mapGetters({
      column_types: 'metadata/getColumnTypesPaymentDashboard'
    }),
    countPayments() {
      return this.localpayments.length
    },
    localPaymentsPaginated() {
      return this.localpayments.slice(0, this.pageSize * this.page)
    },
    cellWidthComputed() {
      let result = Object.values(this.cellWidth).map((column) =>
        column == 'auto' ? 'auto' : `${Math.round(column)}px`
      )
      return result
    },
    availableFields() {
      return this.column_types || []
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    localColumns: {
      get() {
        return this.columns ?? []
      },
      set(value) {
        this.$emit('update:columns', value)
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    },
    hasSumAbleColumns() {
      return !!this.localColumns?.filter((clm) => clm.sum_able).length
    },
    sumColumns() {
      return this.localColumns.map((clm, k) => {
        return {
          ...clm,
          type: clm.sum_able ? clm.type : 'text'
        }
      })
    },
    paymentsSums() {
      const data = {
        id: 'total-sum'
      }

      for (let k in this.localColumns) {
        let column = this.localColumns[k],
          sum = k < 1 ? this.$t('global.total') : null

        if (column.sum_able && k > 0) {
          sum = this.localpayments
            // Return the column value
            .map((inv) => inv[column.field])
            // Get value with number
            .filter((count) => count?.toString()?.match(/[0-9.,]+/))
            // Parse number to float
            .map((count) => parseFloat(count.toString().replace(',', '.')))

          if (sum.length) {
            // Get sum of all value and fixe float to 2 numbers
            sum = parseFloat(sum.reduce((a, b) => a + b, 0).toFixed(2))
          } else {
            sum = null
          }
        }

        data[column.field] = sum
      }

      return data
    },
    paymentsSumsOld() {
      return this.localColumns.map((clm, k) => {
        let sum = !k ? this.$t('global.total') : null

        if (clm.sum_able && k) {
          sum = this.localpayments
            // Return the column value
            .map((inv) => inv[clm.field])
            // Get value with number
            .filter((count) => count?.toString()?.match(/[0-9.,]+/))
            // Parse number to float
            .map((count) => parseFloat(count.toString().replace(',', '.')))

          if (sum.length) {
            // Get sum of all value and fixe float to 2 numbers
            sum = parseFloat(sum.reduce((a, b) => a + b, 0).toFixed(2))
          } else {
            sum = null
          }
        }

        return {
          id: k + 1,
          value: sum,
          [clm.field]: sum,
          column: clm
        }
      })
    }
  },
  watch: {
    payments: {
      immediate: true,
      handler(newPayments) {
        this.localpayments = [...newPayments]
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.tableResized)
    this.tableResized()
  },
  unmounted() {
    window.removeEventListener('resize', this.tableResized)
  },
  methods: {
    tableResized() {
      this.visibleWidthTable = this.$refs['table-payment-container'].offsetWidth
    },
    handleDrag(e) {
      if (!this.localRequireSaveDashboard) {
        this.localRequireSaveDashboard = true
      }
    },
    handleMove(e) {
      return e.related.previousSibling !== null
    },
    localCellWidth(index) {
      if (this.cellWidth[index] > 0) {
        return `${this.cellWidth[index]}px`
      }

      return 'auto'
    },
    handleDeleteColumns(idx) {
      this.columns.splice(idx, 1)
      this.updateDashboard(false)
    },
    updateDashboard(value) {
      this.$emit('update:dashboard', value)
    },
    handleSortColumn({ column, direction }) {
      if (direction === '-') {
        this.$emit('refresh-click')
        return
      }
      this.currentSort = column

      let sortedModels = [...this.localpayments]

      sortedModels = sortedModels.sort((a, b) => {
        console.log(typeof a[column])
        const fa =
          a[column]?.value?.toLowerCase() ||
          a[column]?.id?.toLowerCase() ||
          a[column]?.toLowerCase()
        const fb =
          b[column]?.value?.toLowerCase() ||
          b[column]?.id?.toLowerCase() ||
          b[column]?.toLowerCase()

        if (fa < fb) {
          return direction === 'up' ? -1 : 1
        }
        if (fa > fb) {
          return direction === 'up' ? 1 : -1
        }
        return 0
      })

      this.localpayments = sortedModels
    }
  }
}
</script>
